import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Chip,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import { Subscription } from '../../types';
import {
  subscriptionService,
  paymentService,
  PaymentMethod,
  SubscriptionPreview
} from '../../services/subscription';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';

interface ManageAddOnsDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (addOns: AddOnSelection[], paymentMethodId?: string) => Promise<{ success: boolean; error?: any }>;
  subscription: Subscription | null;
  organizationId: string;
}

interface AddOnSelection {
  priceId: string;
  quantity: number;
  name: string;
  pricePerPack: number;
}

interface AddOnPack {
  type: string;
  packSize: number;
  name: string;
  description: string;
  pricing: {
    monthly: {
      priceCents: number;
      stripePriceId: string;
    };
    yearly: {
      priceCents: number;
      stripePriceId: string;
      displayPricePerMonth?: number;
    };
  };
  recommended: boolean;
  // Legacy fields for backward compatibility
  monthlyPriceCents: number;
  stripePriceId: string;
}

// Helper to get the correct price based on billing interval
const getAddOnPrice = (pack: AddOnPack, billingInterval: 'monthly' | 'yearly'): number => {
  return billingInterval === 'yearly'
    ? pack.pricing.yearly.priceCents
    : pack.pricing.monthly.priceCents;
};

// Helper to get the correct Stripe price ID based on billing interval
const getAddOnPriceId = (pack: AddOnPack, billingInterval: 'monthly' | 'yearly'): string => {
  return billingInterval === 'yearly'
    ? pack.pricing.yearly.stripePriceId
    : pack.pricing.monthly.stripePriceId;
};

const ManageAddOnsDialog: React.FC<ManageAddOnsDialogProps> = ({
  open,
  onClose,
  onSubmit,
  subscription,
  organizationId
}) => {
  const [selectedAddOns, setSelectedAddOns] = useState<Map<string, number>>(new Map());
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('default');
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [isLoadingAddOns, setIsLoadingAddOns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userPackages, setUserPackages] = useState<AddOnPack[]>([]);
  const [storagePackages, setStoragePackages] = useState<AddOnPack[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [preview, setPreview] = useState<SubscriptionPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Get billing interval from subscription (defaults to monthly)
  const billingInterval: 'monthly' | 'yearly' = subscription?.billingInterval || 'monthly';

  // Load available add-ons from API
  useEffect(() => {
    if (open) {
      setIsLoadingAddOns(true);
      subscriptionService.listAvailableAddOns()
        .then(data => {
          setUserPackages(data.userPackages);
          setStoragePackages(data.storagePackages);
        })
        .catch(err => {
          console.error('Failed to fetch available add-ons:', err);
          setError('Failed to load available add-ons. Please try again.');
        })
        .finally(() => {
          setIsLoadingAddOns(false);
        });
    }
  }, [open]);

  // Load payment methods when dialog opens
  useEffect(() => {
    if (open && organizationId) {
      setIsLoadingPaymentMethods(true);
      paymentService.listPaymentMethods(organizationId)
        .then(methods => {
          setPaymentMethods(methods);
          // Pre-select default payment method
          const defaultMethod = methods.find(m => m.isDefault);
          if (defaultMethod) {
            setSelectedPaymentMethod(defaultMethod.id);
          }
        })
        .catch(err => {
          console.error('Failed to fetch payment methods:', err);
          setError('Failed to load payment methods. You may need to add a payment method first.');
        })
        .finally(() => {
          setIsLoadingPaymentMethods(false);
        });
    }
  }, [open, organizationId]);

  // Fetch preview when selections change
  useEffect(() => {
    if (!subscription || selectedAddOns.size === 0) {
      setPreview(null);
      return;
    }

    const fetchPreview = async () => {
      setIsLoadingPreview(true);
      try {
        // Convert selections to add-ons array
        const addOns = Array.from(selectedAddOns.entries()).map(([priceId, quantity]) => ({
          stripePriceId: priceId,
          quantity
        }));

        const previewData = await subscriptionService.previewSubscriptionChange(
          subscription.id,
          undefined,
          undefined,
          billingInterval,
          addOns
        );

        setPreview(previewData);
      } catch (err) {
        console.error('Failed to fetch preview:', err);
        // Don't show error to user, just clear preview
        setPreview(null);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    // Debounce the preview fetch
    const timeoutId = setTimeout(fetchPreview, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedAddOns, subscription, billingInterval]);

  const handleQuantityChange = (priceId: string, quantity: number) => {
    const newSelections = new Map(selectedAddOns);
    if (quantity > 0) {
      newSelections.set(priceId, quantity);
    } else {
      newSelections.delete(priceId);
    }
    setSelectedAddOns(newSelections);
    setError(null);
  };

  const handlePurchaseClick = () => {
    setError(null);

    // Validation
    if (selectedAddOns.size === 0) {
      setError('Please select at least one add-on');
      return;
    }

    if (paymentMethods.length === 0) {
      setError('No payment methods available');
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmPurchase = async () => {
    setShowConfirmDialog(false);
    setError(null);
    setIsSubmitting(true);

    try {
      // Convert selections to AddOnSelection array
      const addOnSelections: AddOnSelection[] = [];

      selectedAddOns.forEach((quantity, priceId) => {
        // Find the pack details - need to search by the correct price ID for current billing interval
        const allPacks = [...userPackages, ...storagePackages];
        const pack = allPacks.find(p => getAddOnPriceId(p, billingInterval) === priceId);

        if (pack && quantity > 0) {
          addOnSelections.push({
            priceId: priceId, // Use the actual selected price ID (monthly or yearly)
            quantity,
            name: pack.name,
            pricePerPack: getAddOnPrice(pack, billingInterval)
          });
        }
      });

      const paymentMethodId = selectedPaymentMethod !== 'default' ? selectedPaymentMethod : undefined;
      const result = await onSubmit(addOnSelections, paymentMethodId);

      if (result.success) {
        handleClose();
      } else {
        setError(result.error?.message || 'Failed to purchase add-ons');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedAddOns(new Map());
      setSelectedPaymentMethod('default');
      setError(null);
      setShowConfirmDialog(false);
      onClose();
    }
  };

  const formatPrice = (priceCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(priceCents / 100);
  };

  const calculateTotal = () => {
    let total = 0;
    const allPacks = [...userPackages, ...storagePackages];

    selectedAddOns.forEach((quantity, priceId) => {
      const pack = allPacks.find(p => getAddOnPriceId(p, billingInterval) === priceId);
      if (pack) {
        total += getAddOnPrice(pack, billingInterval) * quantity;
      }
    });
    return total;
  };

  const formatPaymentMethod = (pm: PaymentMethod) => {
    if (pm.type === 'card') {
      return `${pm.brand || 'Card'} •••• ${pm.last4} (${pm.expiryMonth}/${pm.expiryYear})`;
    }
    return `${pm.type} •••• ${pm.last4}`;
  };

  const defaultPaymentMethod = paymentMethods.find(m => m.isDefault);

  const renderAddOnPack = (pack: AddOnPack) => {
    const priceId = getAddOnPriceId(pack, billingInterval);
    const quantity = selectedAddOns.get(priceId) || 0;

    return (
      <Card key={priceId} variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {pack.name}
                </Typography>
                {pack.recommended && (
                  <Chip label="Recommended" size="small" color="primary" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {pack.description}
              </Typography>
              <Typography variant="body1" color="primary" fontWeight="medium">
                {formatPrice(getAddOnPrice(pack, billingInterval))}{billingInterval === 'yearly' ? '/year' : '/month'} per pack
              </Typography>
            </Box>

            <TextField
              type="number"
              label="Quantity"
              size="small"
              name={`quantity-${priceId}`}
              id={`quantity-${priceId}`}
              value={quantity}
              onChange={(e) => {
                const newQuantity = parseInt(e.target.value) || 0;
                handleQuantityChange(priceId, newQuantity);
              }}
              inputProps={{ min: 0, max: 99 }}
              sx={{ width: 100 }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Manage Add-ons
        {subscription && (
          <Typography variant="body2" color="text.secondary">
            {subscription.name || 'Subscription'}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 3, mb: 0 }}>
            {error}
          </Alert>
        )}

        {/* Two-column layout */}
        <Box sx={{ display: 'flex', minHeight: 500, maxHeight: '70vh' }}>
          {/* Left Column - Scrollable Add-ons List */}
          <Box sx={{
            flex: 1,
            p: 3,
            overflowY: 'auto',
            borderRight: '1px solid',
            borderColor: 'divider'
          }}>
            {isLoadingAddOns ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* User Add-ons Section */}
                {userPackages.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <GroupIcon color="primary" />
                      <Typography variant="h6">Concurrent Users</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {userPackages.map(pack => renderAddOnPack(pack))}
                    </Box>
                  </Box>
                )}

                {userPackages.length > 0 && storagePackages.length > 0 && (
                  <Divider sx={{ my: 3 }} />
                )}

                {/* Storage Add-ons Section */}
                {storagePackages.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <StorageIcon color="primary" />
                      <Typography variant="h6">Storage</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {storagePackages.map(pack => renderAddOnPack(pack))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Right Column - Sticky Summary Panel */}
          <Box sx={{
            width: 320,
            p: 3,
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            <Typography variant="h6" fontWeight="medium">
              Order Summary
            </Typography>

            {/* Selected Add-ons Summary */}
            {selectedAddOns.size > 0 ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Selected Add-ons
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Array.from(selectedAddOns.entries()).map(([priceId, quantity]) => {
                    const allPacks = [...userPackages, ...storagePackages];
                    const pack = allPacks.find(p => getAddOnPriceId(p, billingInterval) === priceId);
                    if (!pack || quantity === 0) return null;

                    const subtotal = getAddOnPrice(pack, billingInterval) * quantity;
                    return (
                      <Box key={priceId} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">
                          {pack.name} × {quantity}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(subtotal)}{billingInterval === 'yearly' ? '/year' : '/mo'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Select add-ons to see pricing
              </Alert>
            )}

            {/* Payment Method Selection */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Payment Method
              </Typography>

              {isLoadingPaymentMethods ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : paymentMethods.length === 0 ? (
                <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                  No payment methods found. Please add one first.
                </Alert>
              ) : (
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={selectedPaymentMethod}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  >
                    {paymentMethods.map((pm) => (
                      <FormControlLabel
                        key={pm.id}
                        value={pm.id}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {formatPaymentMethod(pm)}
                            </Typography>
                            {pm.isDefault && (
                              <Chip label="Default" size="small" color="primary" variant="outlined" />
                            )}
                          </Box>
                        }
                        sx={{
                          border: '1px solid',
                          borderColor: selectedPaymentMethod === pm.id ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          mb: 1,
                          p: 0.5,
                          bgcolor: selectedPaymentMethod === pm.id ? 'action.selected' : 'background.paper'
                        }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            </Box>

            {/* Total Summary - Stripe Accurate Pricing */}
            {selectedAddOns.size > 0 && (
              <Box sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '2px solid',
                borderColor: 'primary.main'
              }}>
                {isLoadingPreview ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : preview ? (
                  <>
                    {/* Show subtotal (before tax) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {preview.proration_cents !== 0 ? 'Subtotal (Prorated)' : 'Subtotal'}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(preview.subtotal_cents)}
                      </Typography>
                    </Box>

                    {/* Show tax if applicable */}
                    {preview.tax_cents > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Tax</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(preview.tax_cents)}
                        </Typography>
                      </Box>
                    )}

                    {/* Show recurring total if different from immediate charge */}
                    {preview.proration_cents !== 0 && preview.recurring_total_cents > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Next Billing Cycle</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(preview.recurring_total_cents)}{billingInterval === 'yearly' ? '/year' : '/mo'}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {preview.proration_cents !== 0 ? 'Charge Today' : 'Total'}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {formatPrice(preview.immediate_charge_cents)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {preview.proration_cents !== 0
                        ? 'Prorated for current billing period. Full charges apply next billing cycle.'
                        : 'Added to your next billing cycle'
                      }
                    </Typography>
                  </>
                ) : (
                  // Fallback to calculated pricing if preview fails
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(calculateTotal())}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body1" fontWeight="bold">Total</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {formatPrice(calculateTotal())}{billingInterval === 'yearly' ? '/year' : '/mo'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Estimated. Actual charges may include proration and taxes.
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handlePurchaseClick}
          variant="contained"
          disabled={isSubmitting || selectedAddOns.size === 0 || paymentMethods.length === 0 || isLoadingAddOns}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddCircleOutlineIcon />}
        >
          {isSubmitting ? 'Processing...' : 'Buy Add-ons'}
        </Button>
      </DialogActions>

      {/* Purchase Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Confirm Add-on Purchase"
        content={
          <Box>
            <Typography gutterBottom>
              You are about to purchase the following add-ons:
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              {Array.from(selectedAddOns.entries()).map(([priceId, quantity]) => {
                const allPacks = [...userPackages, ...storagePackages];
                const pack = allPacks.find(p => getAddOnPriceId(p, billingInterval) === priceId);
                if (!pack || quantity === 0) return null;

                const subtotal = getAddOnPrice(pack, billingInterval) * quantity;
                return (
                  <Box key={priceId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {pack.name} × {quantity}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrice(subtotal)}{billingInterval === 'yearly' ? '/year' : '/month'}
                    </Typography>
                  </Box>
                );
              })}
              <Divider sx={{ my: 1.5 }} />

              {/* Show Stripe accurate pricing if available */}
              {preview ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {preview.proration_cents !== 0 ? 'Subtotal (Prorated)' : 'Subtotal'}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrice(preview.subtotal_cents)}
                    </Typography>
                  </Box>
                  {preview.tax_cents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Tax
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(preview.tax_cents)}
                      </Typography>
                    </Box>
                  )}
                  {preview.proration_cents !== 0 && preview.recurring_total_cents > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Next Billing Cycle
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(preview.recurring_total_cents)}{billingInterval === 'yearly' ? '/year' : '/month'}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {preview.proration_cents !== 0 ? 'Charge Today' : 'Total'}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {formatPrice(preview.immediate_charge_cents)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    {formatPrice(calculateTotal())}{billingInterval === 'yearly' ? '/year' : '/month'}
                  </Typography>
                </Box>
              )}

              {defaultPaymentMethod && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Payment method: {defaultPaymentMethod.brand?.toUpperCase()} •••• {defaultPaymentMethod.last4}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {preview && preview.proration_cents !== 0
                ? `You'll be charged ${formatPrice(preview.immediate_charge_cents)} today (prorated). The recurring charge of ${formatPrice(preview.recurring_total_cents)} will apply on your next billing cycle.`
                : `These charges will be added to your next billing cycle and will recur ${billingInterval === 'yearly' ? 'annually' : 'monthly'}.`
              }
            </Typography>
          </Box>
        }
        confirmText="Confirm Purchase"
        cancelText="Go Back"
        onConfirm={handleConfirmPurchase}
        onCancel={() => setShowConfirmDialog(false)}
        severity="info"
        isLoading={isSubmitting}
      />
    </Dialog>
  );
};

export default ManageAddOnsDialog;
